import { Injectable } from '@angular/core';

import { WindowRef } from '@frontend/vanilla/core';

import { CardPaymentMethod, DepositSubmitRequest, GooglePayConfig } from '../model/depositInfo.model';
import { GPAY_CONSTANTS } from '../shared/error.constants';
import { BetslipQdApiService } from './betslipqd-api.service';
import { DataService } from './data.service';
import { DepositService } from './deposit.service';

@Injectable({
    providedIn: 'root',
})
export class GooglePayService {
    googlePayConfiguration: GooglePayConfig;
    environment: any;
    allowedCardNetworks: any;
    isCreditCardBanEnabled: any;
    feId: any;
    gpayPublicKey: any;
    googlePayClient: any;
    interVal: any;
    showGpayButton: boolean;
    gpayMerchantId: any;
    gpayMerchantName: any;
    tokenUrl: any;
    paymentDetailsInfo: any;
    callBackMethod: any;
    constructor(
        private dataService: DataService,
        private WindowRef: WindowRef,
        private apiService: BetslipQdApiService,
        private depositService: DepositService,
    ) {}
    loadUserInfo(res: any, callBackMethod: any) {
        this.callBackMethod = callBackMethod;
        this.environment = res?.configParams?.gpay_environment || 'TEST';
        if (res?.configParams?.supported_networks) {
            let supportedNetworksSplit = res?.configParams?.supported_networks.split(',');
            if (supportedNetworksSplit?.length > 0) {
                supportedNetworksSplit = supportedNetworksSplit.map((name: any) => name.toUpperCase());
                this.allowedCardNetworks = supportedNetworksSplit;
            }
        } else {
            this.allowedCardNetworks = ['MASTERCARD', 'VISA', 'MAESTRO', 'AMEX'];
        }
        this.isCreditCardBanEnabled = res?.configParams?.creditCardBanEnabled === 'Y';
        this.feId = res?.userInfo?.labelInfo?.feId;
        if (res?.configParams?.load_gpay_script === 'Y' && res?.configParams?.gpayPublicKey) {
            this.gpayPublicKey = res?.configParams?.gpayPublicKey;
            this.loadGpayApi();
        } else {
            this.callBackMethod();
        }
    }
    loadGpayApi() {
        if (document.getElementById('gpayPlugin')) {
            return;
        }
        const body = <HTMLDivElement>document.body;
        const script = document.createElement('script');
        script.innerHTML = '';
        script.src = GPAY_CONSTANTS.GPAY_SCRIPT;
        script.async = false;
        script.defer = true;
        script.id = 'gpayPlugin';
        script.type = 'text/javascript';
        script.onload = this.onGooglePayLoaded.bind(this);
        if (typeof script != 'undefined') {
            (document.getElementsByTagName('head')[0] || body).appendChild(script);
        }
    }
    onGooglePayLoaded() {
        this.googlePayClient = new this.WindowRef.nativeWindow['google'].payments.api.PaymentsClient({
            environment: this.environment,
        });
        this.isReadyToPay();
        let isetvalueCount = 0;
        const messageData = sessionStorage.getItem('messageToCashierdata');
        this.interVal = setInterval(() => {
            if (messageData) {
                const data: any = JSON.parse(messageData);
                if (data?.eventName === 'gPayAvailabilityStatus') {
                    this.showGpayButton = data?.parameters?.gpayAllowed ?? false;
                }
                this.handleCallBackandCleanup();
            }
            isetvalueCount = isetvalueCount + 1;
            if (isetvalueCount > 100) {
                this.handleCallBackandCleanup();
                return;
            }
        }, 100);
    }
    handleCallBackandCleanup() {
        sessionStorage.removeItem('messageToCashierdata');
        clearInterval(this.interVal);
        this.callBackMethod();
    }

    isReadyToPay() {
        try {
            const event = {
                eventName: 'gPayAvailability',
                parameters: this.getGooglePayConfig(),
            };
            if ((window as any)?.NativeBridge?.gpayEvents && GPAY_CONSTANTS.GPAY_LABELS.includes(this.feId)) {
                (window as any).NativeBridge.gpayEvents(JSON.stringify(event));
            } else if ((window as any)?.JsNativeBridge?.sendMessageToNative) {
                (window as any).JsNativeBridge.sendMessageToNative(JSON.stringify(event));
            } else {
                this.callBackMethod();
            }
        } catch {
            this.callBackMethod();
        }
    }
    getGooglePayConfig(): any {
        const allowedCardAuthMethods = [GPAY_CONSTANTS.GPAY_AUTHMETHOD];
        const cardPaymentMethod: CardPaymentMethod = {
            type: GPAY_CONSTANTS.GPAY_CARD,
            parameters: {
                allowedAuthMethods: allowedCardAuthMethods,
                allowedCardNetworks: this.allowedCardNetworks,
            },
            tokenizationSpecification: {
                type: 'DIRECT',
                parameters: {
                    protocolVersion: 'ECv2',
                    publicKey: this.gpayPublicKey,
                },
            },
        };
        cardPaymentMethod.parameters.allowCreditCards = !this.isCreditCardBanEnabled;
        this.googlePayConfiguration = {
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: [cardPaymentMethod],
        };
        return this.googlePayConfiguration;
    }
    /* Google pay methods */
    createAndAddGpayButton(selectedOption: any, callBackMethod: any) {
        this.paymentDetailsInfo = selectedOption;
        setTimeout(() => {
            const container = document.getElementById('container');
            this.googlePayClient = this.getGooglePayClient();
            const googlePayButton = this.googlePayClient.createButton({
                onClick: callBackMethod.bind(this),
                buttonColor: 'default',
                buttonType: 'plain',
                buttonSizeMode: 'fill',
            });
            if (container?.children?.length == 0) container.appendChild(googlePayButton);
        }, 0);
    }
    loadPaymentMethods(feId: string) {
        this.depositService.getAmountInTxnCurrency();
        if (this.paymentDetailsInfo?.paymentAccountInfo) {
            this.gpayMerchantId = this.paymentDetailsInfo?.paymentAccountInfo?.GPAY_PROVIDER_MERCHANT_ID || '';
            this.gpayMerchantName = this.paymentDetailsInfo?.paymentAccountInfo?.GPAY_PROVIDER_MERCHANT_NAME || '';
        }
        const paymentDataRequest: GooglePayConfig = {
            ...this.getGooglePayConfig(),
            merchantInfo: {
                merchantId: this.gpayMerchantId,
                merchantName: this.gpayMerchantName,
            },
            transactionInfo: {
                totalPriceStatus: GPAY_CONSTANTS.GPAY_FINAL,
                totalPrice: this.depositService?.depositInfo?.amountInfo?.nativeAmountString?.toString(),
                currencyCode: 'USD',
                transactionId: Date.now().toString(),
            },
        };
        const event = {
            eventName: 'initiategPay',
            parameters: paymentDataRequest,
        };
        if ((window as any).NativeBridge && (window as any).NativeBridge.gpayEvents && GPAY_CONSTANTS.GPAY_LABELS.includes(feId)) {
            (window as any).NativeBridge.gpayEvents(JSON.stringify(event));
        } else if ((window as any).JsNativeBridge && (window as any).JsNativeBridge.sendMessageToNative) {
            (window as any).JsNativeBridge.sendMessageToNative(JSON.stringify(event));
        }
    }
    loadGpayPaymentResponse() {
        this.interVal = setInterval(() => {
            const messageData = sessionStorage.getItem('messageToCashierdata');
            if (messageData) {
                const data: any = JSON.parse(messageData);
                if (data?.eventName === 'gPayPaymentResponse') {
                    if (data.parameters?.paymentData) {
                        this.registerGpay(JSON.parse(data.parameters.paymentData));
                        sessionStorage.removeItem('messageToCashierdata');
                        clearInterval(this.interVal);
                    } else {
                        clearInterval(this.interVal);
                        this.apiService.get('/paymentservice/deposit/console-log?log=Exception occured in GooglePay deposit Submit').subscribe(
                            () => {},
                            () => {},
                        );
                    }
                }
            }
        }, 100);
    }
    registerGpay(data: any) {
        this.tokenUrl = this.paymentDetailsInfo?.paymentAccountInfo?.paymentAccountUpdateUrl || '';
        const tokenParamIndex = this.tokenUrl.lastIndexOf('?token');
        if (data && data.paymentMethodData) {
            const endDigits = data.paymentMethodData.info.cardDetails;
            const cardNetwork = data.paymentMethodData.info.cardNetwork;
            const name = data.shippingAddress && data.shippingAddress.name ? data.shippingAddress.name : '';
            const tokenData = data.paymentMethodData.tokenizationData.token;
            let formattedUrl;
            const urlparams = `/gPayDecryptAndRegister?endDigits=${endDigits}&cardHolderName=${name}&cardType=${cardNetwork}`;
            if (tokenParamIndex != -1) {
                formattedUrl = this.tokenUrl.substring(0, tokenParamIndex) + urlparams + '&' + this.tokenUrl.substring(tokenParamIndex + 1);
            } else {
                formattedUrl = this.tokenUrl + urlparams;
            }
            const tokenRequest = {
                gpayToken: tokenData,
            };
            this.depositService
                .registerCardDetails(formattedUrl, tokenRequest, this.paymentDetailsInfo?.paymentAccountInfo?.cardToken)
                .then((res: any) => {
                    const id = res['body'].accounts[0].id;
                    this.createGpaySubmitReq(id, endDigits, cardNetwork);
                });
        }
    }
    createGpaySubmitReq(paymentAccountId: any, cardEndDigits: any, cardNetwork: any) {
        const tempToken: string = sessionStorage.getItem('tempToken') || '';
        const gPayData: DepositSubmitRequest = {
            paymentMethod: this.paymentDetailsInfo?.paymentMethodInfo?.paymentMethod,
            amountInfo: {
                nativeCurrency: this.depositService?.depositInfo?.amountInfo?.nativeCurrency,
                nativeAmountString: this.depositService?.depositInfo?.amountInfo?.nativeAmountString,
            },
            bonusInfo: {},
            browserInfo: {
                browserLanguage: this.depositService.depositInfo?.browserInfo?.browserLanguage,
                browserScreenHeight: this.depositService.depositInfo?.browserInfo?.browserScreenHeight,
                browserScreenWidth: this.depositService.depositInfo?.browserInfo?.browserScreenWidth,
                browserTimeZone: this.depositService.depositInfo?.browserInfo?.browserTimeZone,
            },
            germanAMLChangesEnabled: false,
            paymentAccountInfo: {
                paymethodID: '',
                paymentAccountId,
                cardType: cardNetwork,
                originalCardEndDigits: cardEndDigits,
                tempToken,
            },
            productPreference: this.dataService.sessionInfo?.productId,
            worldPaySessionIdStatus: false,
            pageName: `${this.paymentDetailsInfo.paymentMethodInfo?.paymentMethod}_input`,
        };
        this.depositService.submitDeposit(gPayData);
    }
    getGooglePayClient() {
        return this.googlePayClient;
    }
    showGooglePayButton() {
        return this.showGpayButton;
    }
}
